import React from "react";
import {
  Badge,
  Box,
  HStack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { Logo } from "../Logo";

export const formatPaymentCardNumber = (value?: string | null) =>
  String(value || "")
    .replace(/[\s-]/g, "")
    .replace(/(.{4})(?=.)/g, "$1-");

export const formatPaymentCardAmount = (value?: number | null) =>
  value ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

export const formatPaymentCardDate = (value?: string | Date | null) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

type PaymentCardVisualProps = {
  cardNumber?: string | null;
  primaryDate?: {
    label: string;
    value?: string | Date | null;
  } | null;
  secondaryDate?: {
    label: string;
    value?: string | Date | null;
  } | null;
  amount?: string;
  fundName?: string | null;
};

export const PaymentCardVisual: React.FC<PaymentCardVisualProps> = ({
  cardNumber,
  primaryDate,
  secondaryDate,
  amount,
  fundName,
}) => {
  const overlayGradient = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.45), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249,168,166,0.45), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.22), transparent 50%)",
  );

  return (
    <Box
      position="relative"
      minH={{ base: "208px", md: "240px" }}
      borderRadius="28px"
      overflow="hidden"
      color="white"
      px={{ base: 4, md: 5 }}
      py={{ base: 4, md: 5 }}
      bgImage={`${overlayGradient}, linear-gradient(135deg, #6f0805 0%, #ad0b06 48%, #d43c35 100%)`}
      boxShadow="0 30px 70px rgba(104, 11, 8, 0.28)"
    >
      <Box
        position="absolute"
        inset={0}
        bg="linear-gradient(145deg, rgba(255,255,255,0.18), rgba(255,255,255,0.02) 42%, rgba(0,0,0,0.16) 100%)"
      />
      <Box
        position="absolute"
        top="-24%"
        left="-8%"
        w="52%"
        h="62%"
        borderRadius="full"
        bg="whiteAlpha.180"
        filter="blur(20px)"
      />
      <Box
        position="absolute"
        bottom="-32%"
        right="-10%"
        w="54%"
        h="64%"
        borderRadius="full"
        bg="blackAlpha.220"
        filter="blur(24px)"
      />

      <VStack
        position="relative"
        align="stretch"
        justify="space-between"
        h="100%"
        spacing={5}
      >
        <HStack justify="space-between" align="center" spacing={2}>
          <VStack align="flex-start" spacing={0}>
            <Text
              textAlign="right"
              fontSize={{ base: "md", md: "xl" }}
              fontWeight="900"
            >
              کارت هدیه
            </Text>
            <Text textAlign="right" fontSize="sm" color="whiteAlpha.900">
              {fundName || "—"}
            </Text>
          </VStack>
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Box bg="whiteAlpha.180" borderRadius="xl">
              <Logo
                variant="vertical"
                forceMode="dark"
                w={"60px"}
                alt="لوگوی امید آی‌بی"
              />
            </Box>
          </Box>
        </HStack>

        <Text
          textAlign="center"
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="800"
        >
          {amount}
        </Text>

        <Text
          dir="ltr"
          textAlign="center"
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="900"
          letterSpacing={{ base: "0.1em", md: "0.14em" }}
          textShadow="0 1px 10px rgba(0,0,0,0.18)"
        >
          {formatPaymentCardNumber(cardNumber) || "—"}
        </Text>

        <HStack spacing={1} align="center" justify="center">
          <CardDateText dateInfo={primaryDate} />
          {secondaryDate && <Text>|</Text>}
          <CardDateText dateInfo={secondaryDate} />
        </HStack>
      </VStack>
    </Box>
  );
};

const CardDateText = ({
  dateInfo,
}: {
  dateInfo?: {
    label: string;
    value?: string | Date | null;
  } | null;
}) => {
  if (!dateInfo?.value) {
    return <Box minH="20px" />;
  }

  return (
    <Text color="whiteAlpha.900" fontSize="xs" textAlign="center">
      {dateInfo.label}: {formatPaymentCardDate(dateInfo.value)}
    </Text>
  );
};

export const PaymentStatusBadge = ({
  label,
  colorScheme = "gray",
}: {
  label: string;
  colorScheme?: string;
}) => (
  <Badge
    colorScheme={colorScheme}
    variant="solid"
    px={3}
    py={1.5}
    borderRadius="full"
    fontSize="xs"
  >
    {label}
  </Badge>
);
